import { type DragEvent, useState } from 'react';

import { useAiBuilderAttachmentPreparation } from '../components/AiBuilderAttachmentPreparationProvider';
import { useAiBuilderChatComposerSelector } from '../store';
import { deriveAiBuilderComposerPolicy } from '../utils/aiBuilderComposerPolicy';

import { useAiBuilderChatCopy } from './useAiBuilderChatCopy';

const dragHasFiles = (dataTransfer: DataTransfer) =>
  Array.from(dataTransfer.types).includes('Files');

/**
 * 파일을 채팅 패널 어디로든 드래그하면 drop 오버레이를 켜고, 놓으면 첨부한다.
 * 감지 범위는 패널 전체(호출부에서 fileDropZoneProps를 패널 루트에 부착)이며,
 * 첨부 가능 여부는 composer 정책(freeText·비소진·턴 미진행)을 그대로 따른다.
 * 파일이 아닌 드래그는 무시하고, 첨부 불가 상태에서는 오버레이를 띄우지 않는다.
 */
export const useAiBuilderComposerFileDrop = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const {
    draft,
    isGenerating,
    messages,
    placeholder,
    selectedFile,
    turnInProgress,
    usage,
  } = useAiBuilderChatComposerSelector();
  const { canOpenFilePicker, prepareFile } =
    useAiBuilderAttachmentPreparation();
  const copy = useAiBuilderChatCopy();
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const { canUseSourceAttachment, hasActiveTurn } =
    deriveAiBuilderComposerPolicy({
      draft,
      hasSelectedFile: !!selectedFile,
      isGenerating,
      messages,
      placeholder,
      copy,
      turnInProgress,
      usage,
    });
  const canAttachFile =
    enabled &&
    canOpenFilePicker &&
    canUseSourceAttachment &&
    !hasActiveTurn &&
    !selectedFile;

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    if (!canAttachFile) return;
    if (!dragHasFiles(event.dataTransfer)) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsDraggingFile(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }

    setIsDraggingFile(false);
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    if (!dragHasFiles(event.dataTransfer)) return;

    event.preventDefault();
    setIsDraggingFile(false);
    if (!canAttachFile) return;
    prepareFile(event.dataTransfer.files[0]);
  };

  return {
    // 첨부 불가로 상태가 바뀌면 남아있던 오버레이를 즉시 감춘다.
    isDraggingFile: isDraggingFile && canAttachFile,
    fileDropZoneProps: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
};
